import type { ApiFieldError, AppError } from '@/shared/types'

/**
 * `ApiErrorDto` бэка: `error` — стабильный машинный код (`UNAUTHORIZED`, `VALIDATION_FAILED`),
 * `message` — для журнала. Форма единая для всех ручек (02-backend §2).
 */
type ApiErrorBody = {
  statusCode?: number
  error?: string
  message?: string | string[]
  fields?: unknown
  details?: unknown
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * `422 fields` → путь поля → код и параметры. Контракт — `{ code, ...params }` (02-backend §2).
 * Строка — нынешняя форма `ApiErrorDto` (`Record<string, string>`, `BE-AUTH-02`): читается
 * как код без параметров, пока бэк не перешёл.
 */
function readFields(raw: unknown): Record<string, ApiFieldError> | undefined {
  if (!isRecord(raw)) return undefined

  const fields: Record<string, ApiFieldError> = {}
  for (const [path, value] of Object.entries(raw)) {
    if (typeof value === 'string') fields[path] = { code: value }
    else if (isRecord(value) && typeof value.code === 'string') {
      fields[path] = { ...value, code: value.code }
    }
  }
  return Object.keys(fields).length > 0 ? fields : undefined
}

/**
 * Ответ бэка → AppError. Пользователю показываем текст по ключу i18n,
 * сырое message сервера в интерфейс не выносим.
 *
 * Машинный код бэка сохраняется и у 403/404: за ним стоит смысл отказа — «учётная запись
 * отключена», «ограничение учётной записи», «приглашение не найдено». Отличать «нет доступа»
 * и «не найдено» от прочих ошибок — по `status`, а не по коду.
 *
 * `retryAfter` кладёт `baseQuery` из заголовка `Retry-After` ответа `429`; `details` — параметры
 * отказа от бэка (`503 MAINTENANCE` → `until`, `announcement`).
 */
export function normalizeError(error: unknown): AppError {
  // Ошибка RTK Query или любое брошенное значение: поля дальше сужаются по одному
  const candidate = error as
    { status?: number | string; data?: ApiErrorBody; retryAfter?: unknown } | undefined
  // PARSING_ERROR — ответ пришёл, но не JSON (HTML прокси на 502): код ответа — в originalStatus
  const originalStatus: unknown =
    candidate && 'originalStatus' in candidate ? candidate.originalStatus : undefined
  const status =
    typeof candidate?.status === 'number'
      ? candidate.status
      : typeof originalStatus === 'number'
        ? originalStatus
        : undefined
  const data = candidate?.data
  const body = isRecord(data) ? (data as ApiErrorBody) : undefined
  const code = typeof body?.error === 'string' ? body.error : undefined

  // Ответа нет вовсе: обрыв связи или истёк таймаут запроса
  if (candidate?.status === 'FETCH_ERROR' || candidate?.status === 'TIMEOUT_ERROR') {
    return { code: 'NETWORK', messageKey: 'errors.network' }
  }

  if (status === 403) {
    return { code: code ?? 'FORBIDDEN', messageKey: 'errors.forbidden', status }
  }

  if (status === 404) {
    return { code: code ?? 'NOT_FOUND', messageKey: 'errors.notFound', status }
  }

  if (status === 422 || status === 400) {
    const fields = readFields(body?.fields)
    return {
      code: code ?? 'VALIDATION_FAILED',
      messageKey: 'errors.validation',
      status,
      ...(fields ? { fields } : {}),
    }
  }

  const retryAfter = typeof candidate?.retryAfter === 'number' ? candidate.retryAfter : undefined
  const details = isRecord(body?.details) ? body.details : undefined
  return {
    code: code ?? 'UNKNOWN',
    messageKey: 'errors.unknown',
    ...(status !== undefined ? { status } : {}),
    ...(retryAfter !== undefined ? { retryAfter } : {}),
    ...(details ? { details } : {}),
  }
}
